import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Download, X } from 'lucide-react';

interface ToastNotificationProps {
  show: boolean;
  type: 'success' | 'error' | 'info';
  title: string;
  message: string;
  onClose: () => void;
  duration?: number;
  progress?: number;
  elapsedTime?: string;
  persistent?: boolean;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({
  show,
  type,
  title,
  message,
  onClose,
  duration = 5000,
  progress,
  elapsedTime,
  persistent
}) => {
  useEffect(() => {
    if (show && duration > 0 && !persistent) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [show, duration, onClose, persistent]);

  const config = {
    success: {
      icon: CheckCircle,
      bgColor: 'bg-white',
      borderColor: 'border-green-200',
      iconColor: 'text-[#1E7F4F]',
      iconBg: 'bg-green-50'
    },
    error: {
      icon: XCircle,
      bgColor: 'bg-white',
      borderColor: 'border-red-200',
      iconColor: 'text-[#B42318]',
      iconBg: 'bg-red-50'
    },
    info: {
      icon: Download,
      bgColor: 'bg-white',
      borderColor: 'border-[#C2DFF1]',
      iconColor: 'text-[#0084CF]',
      iconBg: 'bg-[#F2F8FC]'
    }
  };

  const { icon: Icon, bgColor, borderColor, iconColor, iconBg } = config[type];

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -20, x: 0 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{ opacity: 0, y: -20, x: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="fixed top-20 right-6 z-50 w-96"
        >
          <div className={`${bgColor} border ${borderColor} rounded-xl shadow-lg overflow-hidden`}>
            <div className="p-4">
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center`}>
                  <Icon size={20} className={iconColor} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-gray-900 mb-0.5">
                    {title}
                  </h4>
                  <p className="text-[10px] text-gray-500 font-medium leading-relaxed truncate">
                    {message}
                  </p>
                  
                  {/* Progress & Time Info */}
                  {(progress !== undefined || elapsedTime) && (
                    <div className="mt-3 space-y-2">
                       <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-tighter">
                          <span className="text-amber-600 italic px-1 bg-amber-50 rounded">{elapsedTime || '00:00'} elapsed</span>
                          <span className="text-gray-900">{Math.round(progress || 0)}%</span>
                       </div>
                       <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden border border-gray-50">
                          <motion.div 
                            className="h-full bg-amber-500 rounded-full"
                            style={{ width: `${progress || 0}%` }}
                            initial={{ width: 0 }}
                            animate={{ width: `${progress || 0}%` }}
                            transition={{ type: "spring", bounce: 0, duration: 0.5 }}
                          />
                       </div>
                    </div>
                  )}
                </div>

                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Auto-dismiss Timer Bar */}
            {duration > 0 && !persistent && (
              <motion.div
                className={`h-1 ${
                  type === 'success' ? 'bg-[#1E7F4F]' :
                  type === 'error' ? 'bg-[#B42318]' :
                  'bg-[#0084CF]'
                }`}
                initial={{ width: '100%' }}
                animate={{ width: '0%' }}
                transition={{ duration: duration / 1000, ease: 'linear' }}
              />
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ToastNotification;
